import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  User, Calendar, FileText, Clock, Award, Plus, Download, Eye,
  AlertTriangle, CheckCircle, XCircle, Euro, Lock, Mail, Phone,
  MapPin, UserCircle, Shield, Briefcase, GraduationCap, AlertCircle,
  FileCheck, FilePlus, Building, Edit2, Save, Loader2, Info
} from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';
import { applyDemiJournees, calculateCalendarDays, getSolidariteInfo } from '../lib/conges';

const DEFAULT_PORTAL_CONFIG = {
  conges_demi_journees: false,
  conges_mode_decompte: 'ouvres',
  journee_solidarite_date: null,
  journee_solidarite_travaillee: false
};

const EMPTY_LEAVE_FORM = {
  type_conge: 'cp',
  date_debut: '',
  date_fin: '',
  nb_jours: 0,
  motif: '',
  demi_jour_debut: false,
  demi_jour_fin: false
};

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EmployeePortal = () => {
  const { user } = useAuth();
  const [portalData, setPortalData] = useState(null);
  const [documents, setDocuments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [submittingLeave, setSubmittingLeave] = useState(false);
  const [portalConfig, setPortalConfig] = useState(DEFAULT_PORTAL_CONFIG);
  const [solidariteInfo, setSolidariteInfo] = useState(null);

  const [leaveForm, setLeaveForm] = useState({ ...EMPTY_LEAVE_FORM });

  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [profileForm, setProfileForm] = useState({
    telephone: '',
    adresse: '',
    code_postal: '',
    ville: '',
    email_perso: '',
    personne_urgence: '',
    tel_urgence: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [portalRes, docsRes, configRes] = await Promise.all([
        axios.get(`${API_URL}/portal/me`),
        axios.get(`${API_URL}/portal/my-documents`),
        axios.get(`${API_URL}/portal/config`).catch(() => ({ data: null }))
      ]);
      setPortalData(portalRes.data);
      setDocuments(docsRes.data);
      setPortalConfig({ ...DEFAULT_PORTAL_CONFIG, ...(configRes.data || {}) });

      // Initialize profile form with employee data
      const emp = portalRes.data?.employee;
      if (emp) {
        setProfileForm({
          telephone: emp.telephone || '',
          adresse: emp.adresse || '',
          code_postal: emp.code_postal || '',
          ville: emp.ville || '',
          email_perso: emp.email_perso || '',
          personne_urgence: emp.personne_urgence || '',
          tel_urgence: emp.tel_urgence || ''
        });
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(getErrorMessage(error, 'Erreur lors du chargement des données'));
    } finally {
      setLoading(false);
    }
  };

  // Calcule le nombre de jours décomptés (jours ouvrés/ouvrables, hors fériés) via l'API,
  // avec repli sur le calcul calendaire, puis applique les demi-journées (même règle que la page Congés).
  const computeLeaveDays = async (form) => {
    const { date_debut, date_fin, demi_jour_debut, demi_jour_fin } = form;
    if (!date_debut || !date_fin) {
      setSolidariteInfo(null);
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/portal/conges/calculate-days`, {
        params: { date_debut, date_fin }
      });
      setLeaveForm(prev => ({ ...prev, nb_jours: applyDemiJournees(res.data.nb_jours, demi_jour_debut, demi_jour_fin) }));
      setSolidariteInfo(getSolidariteInfo(res.data, portalConfig, date_debut, date_fin));
    } catch {
      setLeaveForm(prev => ({ ...prev, nb_jours: applyDemiJournees(calculateCalendarDays(date_debut, date_fin), demi_jour_debut, demi_jour_fin) }));
      setSolidariteInfo(getSolidariteInfo(null, portalConfig, date_debut, date_fin));
    }
  };

  const handleLeaveFieldChange = (field, value) => {
    const newForm = { ...leaveForm, [field]: value };
    setLeaveForm(newForm);
    if (['date_debut', 'date_fin', 'demi_jour_debut', 'demi_jour_fin'].includes(field)) {
      computeLeaveDays(newForm);
    }
  };

  const resetLeaveForm = () => {
    setLeaveForm({ ...EMPTY_LEAVE_FORM });
    setSolidariteInfo(null);
  };

  const handleSubmitLeave = async (e) => {
    e.preventDefault();
    if (submittingLeave) return;
    if (!portalData?.employee) {
      toast.error('Profil employé non trouvé');
      return;
    }
    setSubmittingLeave(true);
    try {
      await axios.post(`${API_URL}/portal/conges`, {
        ...leaveForm,
        nb_jours: parseFloat(leaveForm.nb_jours) || calculateCalendarDays(leaveForm.date_debut, leaveForm.date_fin),
        demi_jour_debut: Boolean(portalConfig.conges_demi_journees && leaveForm.demi_jour_debut),
        demi_jour_fin: Boolean(portalConfig.conges_demi_journees && leaveForm.demi_jour_fin),
        employee_id: portalData.employee.id
      });
      toast.success('Demande de congé soumise avec succès');
      setShowLeaveDialog(false);
      resetLeaveForm();
      await fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Erreur lors de la soumission de la demande'));
    } finally {
      setSubmittingLeave(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (passwordForm.new_password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    setSavingPassword(true);
    try {
      await axios.put(`${API_URL}/portal/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success('Mot de passe modifié avec succès');
      setShowPasswordDialog(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(getErrorMessage(err, 'Erreur lors du changement de mot de passe'));
    } finally {
      setSavingPassword(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await axios.put(`${API_URL}/portal/profile`, profileForm);
      toast.success('Coordonnées mises à jour avec succès');
      setShowProfileDialog(false);
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Erreur lors de la mise à jour'));
    } finally {
      setSavingProfile(false);
    }
  };

  const downloadDocument = async (docId, type, filename) => {
    try {
      let url;
      if (type === 'bulletin') {
        url = `${API_URL}/bulletins-paie/${docId}/pdf`;
      } else if (type === 'employee_doc') {
        url = `${API_URL}/employees/${portalData.employee.id}/documents/${docId}/download`;
      } else {
        url = `${API_URL}/portal/documents/${docId}/pdf`;
      }

      const response = await axios.get(url);
      const content = response.data.content || response.data.data;
      const downloadFilename = response.data.filename || filename || 'document.pdf';

      if (!content) {
        toast.error('Document non disponible');
        return;
      }

      const decoded = atob(content);
      if (decoded.startsWith('<!DOCTYPE') || decoded.startsWith('<html')) {
        const blob = new Blob([decoded], { type: 'text/html' });
        window.open(URL.createObjectURL(blob), '_blank');
      } else {
        const bytes = new Uint8Array(decoded.length);
        for (let i = 0; i < decoded.length; i++) bytes[i] = decoded.charCodeAt(i);
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = downloadFilename;
        a.click();
      }
    } catch (err) {
      console.error('Download error:', err);
      toast.error(getErrorMessage(err, 'Erreur lors du téléchargement'));
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      valide: <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Valide</Badge>,
      alerte: <Badge className="bg-yellow-100 text-yellow-800"><AlertTriangle className="w-3 h-3 mr-1" />À renouveler</Badge>,
      expire: <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Expiré</Badge>
    };
    return badges[status] || <Badge variant="outline">{status}</Badge>;
  };

  const getLeaveStatusBadge = (status) => {
    const badges = {
      demande: <Badge className="bg-yellow-100 text-yellow-800">En attente</Badge>,
      valide_manager: <Badge className="bg-blue-100 text-blue-800">Validé Manager</Badge>,
      valide_rh: <Badge className="bg-green-100 text-green-800">Validé RH</Badge>,
      valide: <Badge className="bg-green-100 text-green-800">Validé</Badge>,
      refuse: <Badge className="bg-red-100 text-red-800">Refusé</Badge>
    };
    return badges[status] || <Badge variant="outline">{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F5156]"></div>
      </div>
    );
  }

  const employee = portalData?.employee;
  const conges = portalData?.conges || { acquis: 0, pris: 0, solde: 0 };
  const SOLDE_SOURCE_LABELS = { paie: 'Logiciel de paie', bulletin: 'Bulletin de paie', theorique: 'Valeur théorique' };
  const formatDateFr = (d) => (d && d.length >= 10 ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : '');
  const congesHistorique = portalData?.conges_historique || [];
  const habilitations = portalData?.habilitations || [];

  // Documents from new endpoint
  const docs = documents?.documents || {};
  const docCounts = documents?.counts || {};

  if (!employee) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <User className="w-7 h-7 text-[#0F5156]" />Mon Espace
        </h1>
        <Card>
          <CardContent className="py-12 text-center">
            <User className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium mb-2">Profil non trouvé</h3>
            <p className="text-slate-500">Votre compte utilisateur n'est pas lié à un profil employé.</p>
            <p className="text-slate-500 mt-2">Contactez votre administrateur RH pour résoudre ce problème.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <User className="w-7 h-7 text-[#0F5156]" />Mon Espace
          </h1>
          <p className="text-slate-500 mt-1">Bienvenue, {employee.prenom} {employee.nom}</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Lock className="w-4 h-4 mr-2" />Mot de passe
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Changer le mot de passe</DialogTitle>
                <DialogDescription>Entrez votre mot de passe actuel puis le nouveau</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label>Mot de passe actuel</Label>
                  <Input
                    type="password"
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm(p => ({ ...p, current_password: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nouveau mot de passe</Label>
                  <Input
                    type="password"
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm(p => ({ ...p, new_password: e.target.value }))}
                    required
                    minLength={8}
                  />
                  <p className="text-xs text-slate-500">Minimum 8 caractères</p>
                </div>
                <div className="space-y-2">
                  <Label>Confirmer le mot de passe</Label>
                  <Input
                    type="password"
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm(p => ({ ...p, confirm_password: e.target.value }))}
                    required
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowPasswordDialog(false)}>
                    Annuler
                  </Button>
                  <Button type="submit" className="bg-[#0F5156]" disabled={savingPassword}>
                    {savingPassword ? 'Enregistrement...' : 'Modifier'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={showLeaveDialog} onOpenChange={(open) => { if (submittingLeave) return; setShowLeaveDialog(open); if (!open) resetLeaveForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#0F5156] hover:bg-[#0d464a]">
                <Plus className="w-4 h-4 mr-2" />Demander un congé
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nouvelle demande de congé</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmitLeave} className="space-y-4">
                <div className="space-y-2">
                  <Label>Type de congé</Label>
                  <Select value={leaveForm.type_conge} onValueChange={(v) => setLeaveForm(p => ({ ...p, type_conge: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cp">Congés payés</SelectItem>
                      <SelectItem value="rtt">RTT</SelectItem>
                      <SelectItem value="sans_solde">Sans solde</SelectItem>
                      <SelectItem value="maladie">Maladie</SelectItem>
                      <SelectItem value="autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date début</Label>
                    <Input
                      type="date"
                      value={leaveForm.date_debut}
                      onChange={(e) => handleLeaveFieldChange('date_debut', e.target.value)}
                      required
                      data-testid="portal-leave-start-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Date fin</Label>
                    <Input
                      type="date"
                      value={leaveForm.date_fin}
                      onChange={(e) => handleLeaveFieldChange('date_fin', e.target.value)}
                      required
                      data-testid="portal-leave-end-date"
                    />
                  </div>
                </div>
                {portalConfig.conges_demi_journees && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="portal_demi_jour_debut"
                        checked={leaveForm.demi_jour_debut}
                        onChange={(e) => handleLeaveFieldChange('demi_jour_debut', e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <Label htmlFor="portal_demi_jour_debut" className="text-sm cursor-pointer">
                        Début l'après-midi (½ jour)
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="portal_demi_jour_fin"
                        checked={leaveForm.demi_jour_fin}
                        onChange={(e) => handleLeaveFieldChange('demi_jour_fin', e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <Label htmlFor="portal_demi_jour_fin" className="text-sm cursor-pointer">
                        Fin le midi (½ jour)
                      </Label>
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Nombre de jours ({portalConfig.conges_mode_decompte === 'ouvrables' ? 'ouvrables' : 'ouvrés'})</Label>
                  <Input
                    type="number"
                    value={leaveForm.nb_jours}
                    onChange={(e) => setLeaveForm(p => ({ ...p, nb_jours: parseFloat(e.target.value) }))}
                    min="0.5"
                    step="0.5"
                  />
                  <p className="text-xs text-slate-500">
                    {portalConfig.conges_mode_decompte === 'ouvrables'
                      ? 'Décompte en jours ouvrables (lundi-samedi, hors jours fériés)'
                      : 'Décompte en jours ouvrés (lundi-vendredi, hors jours fériés)'}
                  </p>
                  {solidariteInfo && (
                    <p className="text-xs text-purple-700 flex items-center gap-1" data-testid="portal-solidarite-info">
                      <Info className="w-3 h-3" />
                      {solidariteInfo}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Motif (optionnel)</Label>
                  <Textarea
                    value={leaveForm.motif}
                    onChange={(e) => setLeaveForm(p => ({ ...p, motif: e.target.value }))}
                    placeholder="Précisez le motif si nécessaire..."
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setShowLeaveDialog(false); resetLeaveForm(); }} disabled={submittingLeave}>
                    Annuler
                  </Button>
                  <Button type="submit" className="bg-[#0F5156]" disabled={submittingLeave} data-testid="portal-leave-submit-btn">
                    {submittingLeave ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Enregistrement…
                      </>
                    ) : (
                      'Soumettre'
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Congés acquis</p>
                <p className="text-2xl font-bold">{conges.acquis || 0} j</p>
              </div>
              <Calendar className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Congés pris</p>
                <p className="text-2xl font-bold">{conges.pris || 0} j</p>
              </div>
              <Clock className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#0F5156] text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white/80">Solde disponible</p>
                <p className="text-2xl font-bold" data-testid="portal-solde-cp">{conges.solde || 0} j</p>
                {(conges.cp_solde_n !== undefined && conges.cp_solde_n !== null) && (
                  <p className="text-xs text-white/70">N : {Number(conges.cp_solde_n).toFixed(1)} j · N-1 : {Number(conges.cp_solde_n1 || 0).toFixed(1)} j</p>
                )}
              </div>
              <CheckCircle className="w-8 h-8 text-white/80" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Documents</p>
                <p className="text-2xl font-bold">
                  {Object.values(docCounts).reduce((a, b) => a + b, 0)}
                </p>
              </div>
              <FileText className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {conges.source && (
        <p className="text-xs text-slate-500 dark:text-slate-400 flex items-start gap-1" data-testid="portal-solde-source">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            Source du solde : {SOLDE_SOURCE_LABELS[conges.source] || conges.source}
            {conges.a_jour_le ? ` (à jour au ${formatDateFr(conges.a_jour_le)})` : ''}
            {conges.en_attente > 0 ? ` · ${conges.en_attente} j en attente de validation` : ''}
            {conges.rtt_solde ? ` · RTT : ${Number(conges.rtt_solde).toFixed(1)} j` : ''}
            {conges.source === 'theorique' && conges.methode ? ` — ${conges.methode}` : ''}
            {(conges.avertissements || []).length > 0 ? ` — ${conges.avertissements.join(' ')}` : ''}
          </span>
        </p>
      )}

      {/* Main Tabs */}
      <Tabs defaultValue="documents" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="conges">Congés</TabsTrigger>
          <TabsTrigger value="habilitations">Habilitations</TabsTrigger>
          <TabsTrigger value="profil">Mon Profil</TabsTrigger>
          <TabsTrigger value="coordonnees">Coordonnées</TabsTrigger>
        </TabsList>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Contrats */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-[#0F5156]" />
                  Contrats et Avenants
                </CardTitle>
                <CardDescription>{docCounts.contrats || 0} document(s)</CardDescription>
              </CardHeader>
              <CardContent>
                {(docs.contrats || []).length === 0 ? (
                  <p className="text-slate-500 text-center py-4">Aucun contrat</p>
                ) : (
                  <div className="space-y-2">
                    {(docs.contrats || []).map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium">{doc.type_contrat} {doc.est_avenant ? '(Avenant)' : ''}</p>
                          <p className="text-sm text-slate-500">
                            Début: {new Date(doc.date_debut).toLocaleDateString('fr-FR')}
                            {doc.date_fin && ` - Fin: ${new Date(doc.date_fin).toLocaleDateString('fr-FR')}`}
                          </p>
                        </div>
                        <Badge variant={doc.statut === 'actif' ? 'default' : 'secondary'}>
                          {doc.statut}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Bulletins de paie */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Euro className="w-5 h-5 text-emerald-600" />
                  Bulletins de paie
                </CardTitle>
                <CardDescription>{docCounts.bulletins_paie || 0} bulletin(s)</CardDescription>
              </CardHeader>
              <CardContent>
                {(docs.bulletins_paie || []).length === 0 ? (
                  <p className="text-slate-500 text-center py-4">Aucun bulletin de paie</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {(docs.bulletins_paie || []).map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Euro className="w-6 h-6 text-emerald-600" />
                          <div>
                            <p className="font-medium">{doc.titre || doc.periode_label || doc.periode}</p>
                            <p className="text-sm text-slate-500">
                              {doc.salaire_net ? `Net à payer : ${Number(doc.salaire_net).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}` : ''}
                              {doc.distribue_le ? `${doc.salaire_net ? ' · ' : ''}Mis à disposition le ${new Date(doc.distribue_le).toLocaleDateString('fr-FR')}` : ''}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadDocument(doc.id, doc.type_doc === 'bulletin' ? 'bulletin' : 'employee_doc', doc.filename)}
                          disabled={doc.type_doc === 'bulletin' && doc.has_pdf === false}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Attestations */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileCheck className="w-5 h-5 text-blue-600" />
                  Attestations
                </CardTitle>
                <CardDescription>{docCounts.attestations || 0} attestation(s)</CardDescription>
              </CardHeader>
              <CardContent>
                {(docs.attestations || []).length === 0 ? (
                  <p className="text-slate-500 text-center py-4">Aucune attestation</p>
                ) : (
                  <div className="space-y-2">
                    {(docs.attestations || []).map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium">{doc.type}</p>
                          <p className="text-sm text-slate-500">
                            {new Date(doc.generated_date).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadDocument(doc.id, 'generated', doc.filename)}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Categories dynamiques */}
            {(() => {
              const portalCategories = documents?.categories || {};
              const specialCats = ['contrat', 'avenant', 'arret_maladie', 'bulletin_paie'];
              return Object.entries(portalCategories)
                .filter(([code]) => !specialCats.includes(code))
                .map(([code, info]) => (
                  <Card key={code}>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full" style={{ backgroundColor: info.color || '#94A3B8' }} />
                        {info.label}
                      </CardTitle>
                      <CardDescription>{docCounts[code] || 0} document(s)</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {(docs[code] || []).length === 0 ? (
                        <p className="text-slate-500 text-center py-4">Aucun document</p>
                      ) : (
                        <div className="space-y-2">
                          {(docs[code] || []).map((doc) => (
                            <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                              <div>
                                <p className="font-medium">{doc.titre}</p>
                                <p className="text-sm text-slate-500">
                                  {new Date(doc.date_document || doc.uploaded_at).toLocaleDateString('fr-FR')}
                                </p>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => downloadDocument(doc.id, 'employee_doc', doc.filename)}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ));
            })()}
          </div>
        </TabsContent>

        {/* Congés Tab */}
        <TabsContent value="conges">
          <Card>
            <CardHeader>
              <CardTitle>Historique des congés</CardTitle>
              <CardDescription>Toutes vos demandes de congés</CardDescription>
            </CardHeader>
            <CardContent>
              {congesHistorique.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Aucune demande de congé</p>
              ) : (
                <div className="space-y-3">
                  {congesHistorique.map((conge) => (
                    <div key={conge.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium">{conge.type_conge}</p>
                        <p className="text-sm text-slate-500">
                          Du {new Date(conge.date_debut).toLocaleDateString('fr-FR')} au {new Date(conge.date_fin).toLocaleDateString('fr-FR')}
                        </p>
                        <p className="text-sm text-slate-500">{conge.nb_jours} jour(s)</p>
                      </div>
                      {getLeaveStatusBadge(conge.statut)}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Habilitations Tab */}
        <TabsContent value="habilitations">
          <Card>
            <CardHeader>
              <CardTitle>Mes habilitations</CardTitle>
              <CardDescription>Certifications, formations et autorisations</CardDescription>
            </CardHeader>
            <CardContent>
              {habilitations.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Aucune habilitation enregistrée</p>
              ) : (
                <div className="space-y-3">
                  {habilitations.map((hab) => (
                    <div key={hab.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Award className="w-8 h-8 text-[#0F5156]" />
                        <div>
                          <p className="font-medium">{hab.type_habilitation}</p>
                          {hab.numero && <p className="text-sm text-slate-500">N° {hab.numero}</p>}
                          <p className="text-sm text-slate-500">
                            Expire le {new Date(hab.date_expiration).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        {getStatusBadge(hab.statut)}
                        <p className="text-sm text-slate-500 mt-1">
                          {hab.jours_restants > 0 ? `${hab.jours_restants} jours restants` : 'Expiré'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Profil Tab */}
        <TabsContent value="profil">
          <Card>
            <CardHeader>
              <CardTitle>Informations professionnelles</CardTitle>
              <CardDescription>Données de votre fiche employé</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label className="text-slate-500">Nom complet</Label>
                    <p className="font-medium text-lg">{employee.prenom} {employee.nom}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Email professionnel</Label>
                    <p className="font-medium">{employee.email}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Matricule</Label>
                    <p className="font-medium">{employee.matricule || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Poste</Label>
                    <p className="font-medium">{employee.poste}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label className="text-slate-500">Département</Label>
                    <p className="font-medium">{employee.departement}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Date d'embauche</Label>
                    <p className="font-medium">{new Date(employee.date_embauche).toLocaleDateString('fr-FR')}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Type de contrat</Label>
                    <p className="font-medium">{employee.type_contrat}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Statut</Label>
                    <Badge variant={employee.statut === 'actif' ? 'default' : 'secondary'}>
                      {employee.statut}
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Coordonnées Tab */}
        <TabsContent value="coordonnees">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Mes coordonnées</CardTitle>
                <CardDescription>Informations de contact personnelles</CardDescription>
              </div>
              <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Edit2 className="w-4 h-4 mr-2" />Modifier
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Modifier mes coordonnées</DialogTitle>
                    <DialogDescription>Mettez à jour vos informations de contact</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleUpdateProfile} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Téléphone</Label>
                        <Input
                          type="tel"
                          value={profileForm.telephone}
                          onChange={(e) => setProfileForm(p => ({ ...p, telephone: e.target.value }))}
                          placeholder="06 00 00 00 00"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Email personnel</Label>
                        <Input
                          type="email"
                          value={profileForm.email_perso}
                          onChange={(e) => setProfileForm(p => ({ ...p, email_perso: e.target.value }))}
                          placeholder="email@personnel.com"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Adresse</Label>
                      <Input
                        value={profileForm.adresse}
                        onChange={(e) => setProfileForm(p => ({ ...p, adresse: e.target.value }))}
                        placeholder="123 rue Example"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Code postal</Label>
                        <Input
                          value={profileForm.code_postal}
                          onChange={(e) => setProfileForm(p => ({ ...p, code_postal: e.target.value }))}
                          placeholder="75001"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Ville</Label>
                        <Input
                          value={profileForm.ville}
                          onChange={(e) => setProfileForm(p => ({ ...p, ville: e.target.value }))}
                          placeholder="Paris"
                        />
                      </div>
                    </div>
                    <div className="border-t pt-4 mt-4">
                      <Label className="text-slate-700 font-medium">Contact d'urgence</Label>
                      <div className="grid grid-cols-2 gap-4 mt-2">
                        <div className="space-y-2">
                          <Label>Nom du contact</Label>
                          <Input
                            value={profileForm.personne_urgence}
                            onChange={(e) => setProfileForm(p => ({ ...p, personne_urgence: e.target.value }))}
                            placeholder="Nom et prénom"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Téléphone d'urgence</Label>
                          <Input
                            type="tel"
                            value={profileForm.tel_urgence}
                            onChange={(e) => setProfileForm(p => ({ ...p, tel_urgence: e.target.value }))}
                            placeholder="06 00 00 00 00"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                      <Button type="button" variant="outline" onClick={() => setShowProfileDialog(false)}>
                        Annuler
                      </Button>
                      <Button type="submit" className="bg-[#0F5156]" disabled={savingProfile}>
                        <Save className="w-4 h-4 mr-2" />
                        {savingProfile ? 'Enregistrement...' : 'Enregistrer'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-slate-400" />
                    <div>
                      <Label className="text-slate-500">Téléphone</Label>
                      <p className="font-medium">{employee.telephone || 'Non renseigné'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-slate-400" />
                    <div>
                      <Label className="text-slate-500">Email personnel</Label>
                      <p className="font-medium">{employee.email_perso || 'Non renseigné'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-slate-400 mt-1" />
                    <div>
                      <Label className="text-slate-500">Adresse</Label>
                      <p className="font-medium">
                        {employee.adresse || 'Non renseignée'}
                        {employee.code_postal && employee.ville && (
                          <><br />{employee.code_postal} {employee.ville}</>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                      <Label className="text-amber-800 font-medium">Contact d'urgence</Label>
                    </div>
                    <p className="font-medium text-amber-900">
                      {employee.personne_urgence || 'Non renseigné'}
                    </p>
                    {employee.tel_urgence && (
                      <p className="text-amber-700">{employee.tel_urgence}</p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EmployeePortal;
